import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/CreateExpenseReportForm'
import { ExpenseStatuses } from '@webapp/modules/expense/entities/enums/ExpenseStatusEnum'
import { type ISettingsRepository } from '@webapp/platform/finance'
import { type IExpenseRepository } from '../../interfaces/IExpenseRepository'
import { ExpenseUseCaseErrors } from '../ExpenseUseCaseErrors'

const isBudgetError = (error: RepoError): boolean => {
  const msg = error.message ?? ''
  return (
    error.status === 400 &&
    (msg === 'Approval will exceed budget.' ||
      msg.startsWith('Self-approving this expense'))
  )
}

export class SubmitExpenseReportUseCase {
  constructor(
    private readonly deps: {
      expenseRepository: IExpenseRepository
      settingsRepository: ISettingsRepository
    }
  ) {}

  execute = async ({
    formValues,
    shouldSkipBudgetCheck,
  }: {
    formValues: ExpenseReportFormSchema
    shouldSkipBudgetCheck: boolean
  }): Promise<{ id: number; selfApproved: boolean }> => {
    try {
      if (formValues.id != null) {
        const draftId = formValues.id
        const result = await this.deps.expenseRepository.updateExpenseRequest(
          draftId,
          formValues,
          ExpenseStatuses.PENDING,
          shouldSkipBudgetCheck
        )
        return { id: draftId, selfApproved: result.selfApproved }
      }

      return await this.deps.expenseRepository.createExpenseRequest(
        formValues,
        ExpenseStatuses.PENDING,
        shouldSkipBudgetCheck
      )
    } catch (err) {
      if (!(err instanceof RepoError) || !isBudgetError(err)) {
        throw err
      }

      if (!shouldSkipBudgetCheck) {
        let isBudgetOverageEnabled: boolean
        try {
          const { data } =
            await this.deps.settingsRepository.getFinanceSettings()
          isBudgetOverageEnabled = data.isBudgetOverageEnabled
        } catch {
          isBudgetOverageEnabled = false
        }

        if (isBudgetOverageEnabled) {
          throw new ExpenseUseCaseErrors({
            name: 'EXPENSE_SUBMIT_OVER_BUDGET_CONFIRMATION_REQUIRED',
            message:
              'Expense submit exceeds budget but overage is allowed with confirmation',
          })
        } else {
          throw new ExpenseUseCaseErrors({
            name: 'EXPENSE_SUBMIT_OVER_BUDGET',
            message: 'Expense submit exceeds budget',
          })
        }
      }

      throw err
    }
  }
}
